<template lang="pug">
div
  v-textarea(
    v-if="myMultiline"
    v-model="content"
    v-bind="$attrs"
    ref="input"
    filled
    :color="color"
    :rows="myMultiline"
    :class="classes"
    :type="myType"
    :label="myTitle"
    :hint="myHint"
    :dense="!isLarge"
    :autocomplete="myAutoComplete"
    :hide-details="hasNoHints"
    :rules="rules"
    :prepend-inner-icon="myIcon"
    :readonly="isReadOnly"
    :required="isRequired && !isDisabled"
    :disabled="isDisabled"
    :append-icon="metaIcon"
    @click:append="revealed = !revealed"
    @keyup.enter="$emit('action-performed', $refs.input)"
  )

  v-text-field.x-widget(
    v-else
    v-model="content"
    v-bind="$attrs"
    ref="input"
    filled
    :color="color"
    :class="classes"
    :type="myType"
    :label="myTitle"
    :hint="myHint"
    :dense="!isLarge"
    :autocomplete="myAutoComplete"
    :hide-details="hasNoHints"
    :rules="rules"
    :prepend-inner-icon="myIcon"
    :readonly="isReadOnly"
    :required="isRequired && !isDisabled"
    :append-icon="metaIcon"
    @click:append="revealed = !revealed"
    @keyup.enter="$emit('action-performed', $refs.input)"
  )
</template>

<style>
.x-widget-readonly fieldset {
  border: none;
}
.x-font-monospace {
  font-family: monospace;
}
</style>

<script>
import { mdiEyeOutline, mdiEyeOffOutline } from '@mdi/js'
import { interpolate, isInterpolated } from '@/helpers/strings'
import { selectOptions, selectColor } from '@/helpers/styles'

export default {

  props: {
    /**
     * Objeto contendo os valores e campos do contexto no qual o widget está
     * inserido para inferência de propriedades aplicadas em interpolação de
     * templates de texto.
     */
    context: Object,

    /**
     * Instância opcional de um campo do Paper.
     * @type {Keep.Paper.Design.Field}
     */
    field: Object,

    /**
     * Um dos tipos de dados suportados pelo Paper.
     * @type {Keep.Paper.Design.TypeNames}
     */
    dataType: String,

    /**
     * Diz se o componente suporta seleção de intervalo.
     * @type {Boolean}
     */
    allowRange: Boolean,

    /**
     * Valor editado no componente.
     * @type {Any}
     */
    value: {
      validator: v => true
    },
  },

  data: () => ({
    icons: {
      mdiEyeOutline,
      mdiEyeOffOutline,
    },

    dataSet: null,  // Um objeto do tipo {Keep.Paper.Design.DataSet}
    failure: null,  // Um objeto do tipo {Keep.Paper.Design.Status}

    revealed: false
  }),

  computed: {
    myName: vm => vm.getIt('name'),
    myTitle: vm => vm.getIt('title') || vm.myName,
    myDescription: vm => vm.getIt('description'),
    myHint: vm => vm.getIt('hint'),
    myPlaceholder: vm => vm.getIt('placeholder'),
    isSecure: vm => vm.hasIt('secure'),
    isLarge: vm => vm.hasIt('large'),
    hasNoHints: vm => vm.hasIt('noHints'),
    isDisabled: vm => vm.hasIt('disabled'),
    isRequired: vm => vm.hasIt('required'),
    isReadOnly: vm => vm.hasIt('readOnly') || vm.hasIt('readonly'),
    myIcon: vm => vm.getIt('icon'),
    myFont: vm => vm.getIt('font'),

    myMultiline: vm => vm.getIt('multiline'),
    isSelect: vm => vm.hasIt('dataSet'),

    myType () { return this.isSecure && !this.revealed ? 'password' : 'text' },
    myAutoComplete () {
      const autocomplete = this.getIt('autocomplete')
      if (autocomplete) return autocomplete
      if (this.myName?.toLowerCase() === 'username') return 'username'
      if (this.myName?.toLowerCase() === 'password') return 'current-password'
      return null
    },

    errorMessages () {
      return this.failure
        ? [ 'Não foi possível carregar os dados.', this.failure.message ]
        : null
    },

    options() {
      const { context } = this
      return selectOptions(this.allStates, context)
    },

    color() {
      return selectColor(this.options.style || this.options.color)
    },

    classes () {
      const classes = {}

      classes['x-widget-readonly'] = this.isReadOnly

      // FontStyle:
      //    Default = 0,
      //    Italic = 1,
      //    Bold = 2,
      //    Overline = 4,
      //    Strikethrough = 8,
      //    Underline = 16,
      //    Monospace = 32,
      const font = this.myFont || 0
      if ((font &  1) ==  1) classes['font-italic'] = true
      if ((font &  2) ==  2) classes['font-weight-bold'] = true
      if ((font &  4) ==  4) classes['text-decoration-overline'] = true
      if ((font &  8) ==  8) classes['text-decoration-line-through'] = true
      if ((font & 16) == 16) classes['text-decoration-underline'] = true
      if ((font & 32) == 32) classes['x-font-monospace'] = true
      return classes
    },

    metaIcon () {
      if (!this.isSecure) return null
      return this.revealed ? this.icons.mdiEyeOffOutline : this.icons.mdiEyeOutline
    },

    content: {
      get () {
        return this.value || null
      },
      set (value) {
        this.$emit('input', value || null)
      }
    },

    rules () {
      const required = (this.isRequired && !this.isDisabled)
      return [
        v => !required || !!v || 'Campo requerido',
        // v => (v && v.length <= 10) || 'Name must be less than 10 characters',
      ]
    },
  },

  watch: {
    $route () {
      this.loadOptionsAsync()
    },
  },

  mounted () {
    this.loadOptionsAsync()
  },

  methods: {
    getIt (property) {
      return this[property]
        || this.$props[property]
        || this.$attrs[property]
        || this.field?.[property]
    },
    hasIt (property) {
      if (this.$props[property] === '') return true
      if (this.$attrs[property] === '') return true
      const value = this[property]
        || this.$props[property]
        || this.$attrs[property]
        || this.field?.[property]
      return !!value
    },

    async loadOptionsAsync () {
      this.dataSet = null
      this.failure = null

      var dataSet = this.getIt('dataSet')
      if (!dataSet?.see) {
        this.dataSet = dataSet
        return
      }

      // Resolvendo a referência ao dataset...
      const result = await this.$resolve(dataSet.see, { noCache: true })
      if (!result.ok) {
        this.failure = result.entity
        return
      }

      this.dataSet = result.entity
    },

    format(text) {
      return isInterpolated(text) ? interpolate(text, this.context) : text
    },
  },
}
</script>